import { makeStyles } from '@mui/styles';
import { neutral3, primary } from 'apollo-react/colors';
import Panel from 'apollo-react/components/Panel';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ResizableBox } from 'react-resizable';

import { EXPRESSION_DATA } from './expressionData';
import ReportModel from './ReportModel';
import EntityAutocompleteTextarea from '../../../components/EntityAutocompleteTextarea';
import FormulaExpressionPicker from '../../../components/FormulaExpressionPicker';
import PanelWrapper from '../../../components/PanelWrapper';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  EXPRESSION_EDITOR_DEFAULT_HEIGHT,
  EXPRESSION_EDITOR_MAX_HEIGHT_COEFFICIENT,
  EXPRESSION_EDITOR_MIN_HEIGHT_COEFFICIENT,
  EXPRESSION_EDITOR_TEXT_FIELD_OFFSET,
} from '../../constants/report-designer.constants';
import { getEntityDefinitionAutocompleteFields } from '../../selectors/report-designer.selectors';

const useStyles = makeStyles({
  container: {
    position: 'relative',
  },
  handleContainer: {
    zIndex: 1,
    position: 'absolute',
    width: '100%',
    padding: 4,
    cursor: 'ns-resize',
    backgroundColor: 'transparent',
    '&:hover div': {
      backgroundColor: primary,
      width: '100%',
      height: 2,
    },
  },
  handle: {
    position: 'absolute',
    right: 0,
    top: -2,
    height: 1,
    width: '100%',
    backgroundColor: neutral3,
  },
  topPanel: {
    height: '100%',
  },
  panelGroup: {
    display: 'flex',
    padding: 0,
    overflow: 'hidden',
    height: '100%',
    '& > div': {
      backgroundColor: 'inherit',
    },
    '& > div:nth-of-type(1) > div': {
      height: '100%',
    },
    '& > div:nth-of-type(2) > div': {
      height: '100%',
    },
  },
  leftPanel: {
    height: 'calc(100% - 20px)',
    marginRight: 20,
  },
  rightPanel: {
    width: '100%',
    height: 'calc(100% - 20px)',
    marginLeft: 20,
  },
  editorPanel: {
    marginTop: 16,
  },
  editorExpression: {
    height: 'calc(100% - 20px)',
    marginTop: 0,
    marginBottom: 0,
    '& > div': {
      marginTop: 0,
    },
    '& div': {
      height: '100%',
    },
    '& textarea': {
      height: '100% !important',
    },
  },
});

const ExpressionModal = ({
  height = EXPRESSION_EDITOR_DEFAULT_HEIGHT,
  onChange,
  expression,
  onBlurEditor,
}) => {
  const classes = useStyles();
  const editorPanelRef = useRef(null);
  const translateString = useTranslation();

  const autocompleteFields = useSelector(getEntityDefinitionAutocompleteFields);

  const [editorExpression, setEditorExpression] = useState(expression);
  const [editorPanelHeight, setEditorPanelHeight] = useState(
    height / 2 - EXPRESSION_EDITOR_TEXT_FIELD_OFFSET
  );

  useEffect(() => {
    setEditorExpression(expression);
  }, [expression]);

  const onResize = (e, data) => {
    setEditorPanelHeight(height - EXPRESSION_EDITOR_TEXT_FIELD_OFFSET - data.size.height);
  };

  const onChangeEditor = ({ target }) => {
    setEditorExpression(target.value);
  };

  return (
    <div style={{ height }}>
      <ResizableBox
        height={height / 2}
        className={classes.container}
        minConstraints={[Infinity, height * EXPRESSION_EDITOR_MIN_HEIGHT_COEFFICIENT]}
        maxConstraints={[Infinity, height * EXPRESSION_EDITOR_MAX_HEIGHT_COEFFICIENT]}
        axis="y"
        resizeHandles={['se']}
        handle={
          <div className={classes.handleContainer}>
            <div className={classes.handle} />
          </div>
        }
        onResize={onResize}
      >
        <div className={classes.topPanel}>
          <PanelWrapper classes={classes}>
            <Panel
              className={classes.leftPanel}
              width="50%"
              minWidth="30%"
              maxWidth="70%"
              resizable
              hideButton
            >
              <ReportModel change={onChange} showSearch />
            </Panel>
            <div className={classes.rightPanel}>
              <FormulaExpressionPicker onNodeSelect={onChange} expressionList={EXPRESSION_DATA} />
            </div>
          </PanelWrapper>
        </div>
      </ResizableBox>
      <div
        className={classes.editorPanel}
        ref={editorPanelRef}
        style={{ height: editorPanelHeight }}
      >
        <div>
          <h4>{translateString('Editor')}</h4>
        </div>
        <EntityAutocompleteTextarea
          value={editorExpression}
          onChange={onChangeEditor}
          onBlur={onBlurEditor}
          autocompleteFields={autocompleteFields}
        />
      </div>
    </div>
  );
};

export default ExpressionModal;
